/**
 * BalanceChart — daily projected balance on a FIXED window
 * [today−12mo … today+12mo] (never moves with the selected month). Adds a
 * selected-month highlight band, a marker for the selected transaction, and
 * click-to-select: clicking anywhere selects the clicked month plus the
 * closest transaction within it. Data shaping + selection logic live in the
 * pure ``fixedChartWindow`` / ``monthBandRange`` / ``closestTxInMonth`` helpers
 * (recharts does not render under jsdom).
 *
 * #2684 Part B — two PRESENTATIONS of the same scope, switched by
 * ``ChartPresentationToggle`` (its third consumer, after Ernteprognose and
 * Preisspiegel) and persisted by the page as ``?pm=``:
 *
 *  - ``kurve``   — the balance curve above (a STOCK: the account level per day);
 *  - ``saeulen`` — Einnahmen/Ausgaben columns per month (the FLOWS), aggregated
 *    from ``ledgerRows`` by the pure ``monthlyColumns``, so the bars and the
 *    Buchungen list below the chart cannot disagree.
 *
 * #2955 §1 — the two presentations span ONE timeline, the fixed
 * ``[todayYm−12mo … todayYm+12mo]`` window, and share ONE zoom/pan state.
 *
 * They did not, and the divergence was silent: the curve used that fixed window
 * while the columns used the ledger's OBSERVED first…last month, so switching to
 * Säulen on WF gained seven months of pre-window history and lost fourteen
 * months of forecast — and, holding separate ``useChartTimeWindow`` instances,
 * threw the zoom/pan away as well. This file used to document that as
 * deliberate ("a day→month index map producing fractional windows"). Once both
 * modes span the same window that objection is dead: each rows array is
 * COMPLETE over the span (days null-padded, months zero-filled), so index ↔ date
 * is a total bijection in each grain and nothing is fractional.
 *
 * The shared state is therefore held ONCE, as a DATE RANGE — the only form that
 * survives the translation — with each mode deriving its own ``IndexWindow`` via
 * ``sharedChartWindow`` and the active one writing dates back through the hook's
 * controlled mode. ``reset`` means the same thing in both: the full ±12 months.
 * Day → month → day is lossy and the month grain snaps OUTWARD, so returning
 * from Säulen to Kurve can leave the window a little wider than you left it —
 * that is the documented, tested direction, not drift.
 *
 * #2743 — each Säule is split INTERNALLY: the ``transfer_internal`` share (own
 * money moved between the farmer's own accounts — 30,6 % of every Einnahmen bar
 * and 28,5 % of every Ausgaben bar on WF prod, peaking at 68,4 %) is drawn as a
 * muted grey sub-segment at the bar's tip, with the operative remainder in the
 * series colour from the baseline outward. The bar's total HEIGHT is unchanged:
 * the rows stay in the gross, because ``CROSSCUTTING_CASH`` folds them into the
 * hero net so it ties to Δ-balance under the #2664 single-account filter, and
 * because #2684's ``rowsInMonth`` reconciliation reads the gross. Only the
 * presentation changed — nothing was dropped.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartColumn, ChartSpline } from 'lucide-react';
import { formatEur } from '../../../utils/formatEur';
import {
  ChartPresentationToggle,
  ChartZoomControls,
  useChartTimeWindow,
  yearBoundaryLines,
  type ChartPresentationOption,
  type IndexWindow,
} from '../../../components/charts';
import {
  chartWindowRange,
  fromDayWindow,
  fromMonthWindow,
  toDayWindow,
  toMonthWindow,
  type DateRange,
} from './sharedChartWindow';
import {
  breachSegments,
  chartYFloor,
  chartDataFloor,
  chartYCeil,
  chartDataCeil,
  kkLimitOffScale,
  clampBandToRange,
  columnBandYm,
  fixedChartWindow,
  monthBandRange,
  splitActualPlan,
  ymRangeBandRange,
} from './balanceChartData';
import { closestTxInMonth } from './chartSelection';
import {
  EXPENSE_RED,
  HATCH_ANGLE_DEG,
  HATCH_STROKE_PX,
  HATCH_TILE_PX,
  HATCH_WASH_OPACITY,
  INCOME_GREEN,
  TOTAL_LINE_BLUE,
  TRANSFER_GREY,
} from './chartColors';
import {
  forecastBoundaryYm,
  hasColumnData,
  monthlyColumns,
  type ChartPresentation,
  type MonthlyColumn,
} from './monthlyColumns';
import type { LedgerRow } from './buildLedgerRows';
import type { DailyBalancePoint } from './types';
import './BalanceChart.css';

/** Handles both ``YYYY-MM-DD`` (daily rows) and ``YYYY-MM`` (monthly columns). */
const monthTick = (iso: string): string => {
  const [y, m] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });
};

const kTick = (v: number): string => `${Math.round(v / 1000)}k`;

const PRESENTATIONS: readonly ChartPresentationOption<ChartPresentation>[] = [
  { value: 'kurve', icon: <ChartSpline size={16} aria-hidden="true" />, label: 'Kurve' },
  { value: 'saeulen', icon: <ChartColumn size={16} aria-hidden="true" />, label: 'Säulen' },
];

/**
 * SVG pattern ids for the Prognose hatch — ONE PER (FILL COLOUR, SIDE) since
 * #2986, and they must stay pairwise distinct: duplicate pattern ids in a single
 * document are invalid and which one wins is browser-defined (the trap #2802 hit
 * in v31.8 when ``RankedBars`` mounted twice). Only one BalanceChart mounts per
 * page (``LiquiditaetsplanungPage.tsx``), so plain literals are enough — this is
 * NOT the ``useId``-scoped hardening ``deckungsbeitrag/estimateMark.tsx`` needed
 * for a component that legitimately mounts many times. The test over these pins
 * only that the literals differ from each other; a second mount would still
 * collide, and the single-mount property is what rules that out.
 *
 * WHY THE NEUTRAL NEEDS TWO (#2986). ``HATCH_ANGLE_DEG`` makes the hatch lean per
 * SIDE of the pair, and the transfer sub-segment is drawn inside BOTH sides, so
 * one shared grey pattern would have had to pick a side and then contradict the
 * cue in the other. Splitting it keeps "angle = which side" true for every
 * hatched segment on the plot — and, as a bonus, makes the two legs of one
 * internal transfer visually distinct, which they never were.
 */
export const COLUMN_HATCH_IDS = {
  income: 'lp-col-hatch-income',
  expense: 'lp-col-hatch-expense',
  /** #2743 — the muted own-account-transfer sub-segment, per side (#2986). */
  transferIncome: 'lp-col-hatch-transfer-income',
  transferExpense: 'lp-col-hatch-transfer-expense',
} as const;

/**
 * The paired-column layout (#2955 §2). Exported so the two test files that see
 * different halves of it — the mocked one, which pins that the component passes
 * these; the real-recharts one, which measures what recharts does with them —
 * cannot drift apart from the chart or from each other.
 *
 * The pair reads as ONE month's two figures only if the gap inside it is
 * clearly smaller than the gap to the next month, which is the whole job of
 * these two numbers. Measured at 600 px over the 25-month window: 2 px within a
 * pair against 13 px between months.
 *
 * ⚠️ ``barSize`` is deliberately NOT set, and it is not a matter of taste.
 * recharts takes a different code path entirely when a bar has an explicit
 * size: ``barCategoryGap`` is then IGNORED (the leftover space is simply
 * centred), so setting both silently discards the second. ``maxBarSize`` is the
 * one that composes — it caps without taking over — and it earns its place at
 * the far end of the zoom, where two visible months over a wide viewport would
 * otherwise draw 150 px slabs.
 */
export const COLUMN_BAR_GAP = 2;
export const COLUMN_BAR_CATEGORY_GAP = '30%';
export const COLUMN_MAX_BAR_SIZE = 28;

/**
 * A diagonal hatch over the series colour, marking the PLANNED part of a bar.
 *
 * The geometry (tile, stroke, wash opacity, per-side angle) lives in
 * ``chartColors.ts`` since #2986, next to the hexes it composites — read the
 * ``INCOME_GREEN`` block there for why the two cannot be documented apart, and
 * ``__tests__/chartColors.test.ts`` for the composited measurements.
 *
 * TWO THINGS HERE ARE LOAD-BEARING AND NEITHER IS OBVIOUS:
 *
 *   • The ``<line>`` carries NO opacity, and the Bars that reference this pattern
 *     carry no ``fillOpacity`` either. A ``fill-opacity`` on the referencing
 *     element scales the whole pattern paint — stripe included — which is the
 *     mechanism that dragged the #2955 pair down to CVD ΔE 4,26 through the very
 *     hatch meant to qualify it. The fade belongs to the wash rect alone.
 *   • ``angleDeg`` comes from ``HATCH_ANGLE_DEG`` per SIDE of the pair, so the
 *     Einnahmen stack leans ``/`` and the Ausgaben stack ``\``. That is the
 *     pair-separating cue the wash cannot supply at any legible opacity.
 */
function ForecastHatch({
  id,
  color,
  angleDeg,
}: {
  id: string;
  color: string;
  angleDeg: number;
}) {
  return (
    <pattern
      id={id}
      patternUnits="userSpaceOnUse"
      width={HATCH_TILE_PX}
      height={HATCH_TILE_PX}
      patternTransform={`rotate(${angleDeg})`}
    >
      <rect
        width={HATCH_TILE_PX}
        height={HATCH_TILE_PX}
        fill={color}
        fillOpacity={HATCH_WASH_OPACITY}
      />
      <line
        x1={0}
        y1={0}
        x2={0}
        y2={HATCH_TILE_PX}
        stroke={color}
        strokeWidth={HATCH_STROKE_PX}
      />
    </pattern>
  );
}

/**
 * The same hatch as a CSS background, for the legend's ``geplant`` swatch.
 *
 * SET INLINE by the component rather than declared in ``BalanceChart.css``, for
 * the reason the solid swatches already are: vitest runs ``css: false``
 * (#2870), so jsdom loads no stylesheet and a gradient living in CSS could not
 * be asserted to match the ``<pattern>`` at all. Deriving it here from the very
 * constants the pattern uses is the stronger guarantee anyway — a stylesheet
 * copy is a second literal to keep in sync by hand.
 *
 * WHY 135deg AND NOT 45deg, for the same visual diagonal: SVG rotates the
 * PATTERN, so a vertical line turned 45° ends up running bottom-left→top-right
 * ("/"). A CSS angle names the direction of the gradient LINE, and the colour
 * bands run PERPENDICULAR to it — so 45deg would draw "\" and 135deg draws "/".
 *
 * WHAT IS DELIBERATELY NOT MIRRORED: the period, the wash, and — since #2986 —
 * the per-side ANGLE. The swatch is 0.625rem ≈ 10 px square, so the plot's 9 px
 * tile would fit ONE stripe into it and read as a single diagonal line rather
 * than as a texture, and a 25 %-opacity wash on a 10 px sample washes out
 * entirely. On the angle: the legend entry names the STATE ("geplant"), which
 * both sides are in, so it draws the single ``/`` of ``HATCH_ANGLE_DEG.income``
 * rather than claiming a side. The angle is a cue read off the PLOT, where the
 * two textures are adjacent and comparable; a lone 10 px swatch has nothing to
 * be compared against. A swatch is a NAME for a texture, not a photograph of it.
 */
const SWATCH_HATCH_PERIOD_PX = 3;
function hatchSwatchBackground(color: string): string {
  const gap = SWATCH_HATCH_PERIOD_PX - HATCH_STROKE_PX;
  return `repeating-linear-gradient(135deg, transparent 0 ${gap}px, ${color} ${gap}px ${SWATCH_HATCH_PERIOD_PX}px)`;
}

/** ``0,4`` → ``40,0 %``, de-DE, with a plain space (an ``Intl`` percent style
 *  emits a NARROW NO-BREAK SPACE whose ICU presence varies by runtime). */
const pct = (share: number): string =>
  `${(share * 100).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;

/**
 * #2743 — the own-account-transfer share of the Säulen currently in scope.
 *
 * ``null`` when the ledger holds no ``transfer_internal`` at all, which is the
 * state of almost every tenant: the segment, its legend and its aria-label
 * clause are then all suppressed rather than naming something not drawn.
 *
 * Computed over the WHOLE column series rather than the zoomed slice, so
 * panning does not make a legend entry blink in and out; the scope is already
 * narrowed by the page's Konto/period filters.
 */
function transferShare(
  columns: readonly MonthlyColumn[],
): { einnahmen: number; ausgaben: number } | null {
  let gIn = 0;
  let tIn = 0;
  let gOut = 0;
  let tOut = 0;
  for (const c of columns) {
    gIn += c.einnahmenEur;
    tIn += c.einnahmenTransferEur;
    gOut += c.ausgabenEur;
    tOut += c.ausgabenTransferEur;
  }
  if (tIn === 0 && tOut === 0) return null;
  return {
    einnahmen: gIn === 0 ? 0 : tIn / gIn,
    ausgaben: gOut === 0 ? 0 : tOut / gOut,
  };
}

/** The legend's id, so the focusable plot region can ``aria-describedby`` it. */
const COLUMN_LEGEND_ID = 'lp-col-legend';

/**
 * The ink of the legend's ``geplant`` swatch (#2955 §3) — the legend's own text
 * grey, deliberately NOT one of the three series colours. The swatch names a
 * TEXTURE that any of them can wear; drawing it in green would quietly assert
 * that "geplant" is a property of Einnahmen.
 *
 * Not exported and not in ``chartColors.ts``: it is chrome for a 10 px DOM
 * square, not a data colour, and the one grey constant that used to live there
 * was retired in §2 for good reasons that a lookalike would blur.
 */
const PLANNED_SWATCH_INK = '#6b7280';

/**
 * HOF-5 — the stroke of the curve's ``<ReferenceLine y={0}>``.
 *
 * Local, not in ``chartColors.ts``, and that placement is the point rather than
 * laziness. ``chartColors.test.ts`` carries a release gate asserting that module
 * exports NOTHING matching /BASELINE/i, because #2955 §2 retired
 * ``ZERO_BASELINE_GREY`` along with the SÄULEN zero rule it stroked: there the
 * axis floor is zero and the Einnahmen bar starts exactly on it, so the new
 * INCOME_GREEN sat at 1,06:1 against the rule and it vanished where a bar met it.
 * That gate is still correct and still guarding column mode.
 *
 * The curve is a different drawing. Its domain floats around zero rather than
 * resting on it, nothing is filled up to the line, and the only ink that ever
 * crosses it is a 2px blue stroke at 8,6:1 — so the geometry that made the
 * Säulen rule invisible cannot arise. Keeping the constant here scopes it to the
 * chart that argued for it, exactly as ``PLANNED_SWATCH_INK`` above is scoped to
 * the swatch that argued for it. The value is the chart's established chrome
 * grey — the same #9ca3af the "Heute" and "Prognose" markers already use — so
 * the plot gains a reference line, not a new colour.
 */
const ZERO_LINE_GREY = '#9ca3af';

/**
 * ``(Einnahmen 40,0 % · Ausgaben 60,0 %)`` — each side NAMED, because a bare
 * ``(40,0 % / 60,0 %)`` gives the reader no way to tell which is which.
 *
 * A side whose share is 0 is omitted rather than printed: under the #2664
 * single-Konto filter only one leg of an internal transfer is in scope, so
 * ``0,0 %`` is the normal state of the other side and reads as a bug. Both zero
 * cannot happen — ``transferShare`` returns ``null`` there.
 */
function transferShareSuffix(share: { einnahmen: number; ausgaben: number }): string {
  const parts: string[] = [];
  if (share.einnahmen > 0) parts.push(`Einnahmen ${pct(share.einnahmen)}`);
  if (share.ausgaben > 0) parts.push(`Ausgaben ${pct(share.ausgaben)}`);
  return parts.length ? ` (${parts.join(' · ')})` : '';
}

/**
 * The Säulen tooltip — a CUSTOM content rather than the default series list.
 *
 * Two reasons, both consequences of #2743's split. First, recharts' default
 * tooltip lists one row per ``<Bar>`` and keeps zero-valued ones (its
 * ``filterNull`` drops only ``null``), so a month — or, under the #2664
 * single-Konto filter, a whole SIDE, since only one leg of an internal transfer
 * is then in scope — would carry a permanent ``Umbuchung … 0,00 €`` row that is
 * invisible in the plot. Second, once the bar is split, the default list shows
 * the two segments and NEVER the gross, leaving the farmer to add up the very
 * figure the bar's height and the Buchungen list are supposed to agree on.
 *
 * So the gross leads and the transfer is a ``davon`` sub-line, present only when
 * it is non-zero. Exported for test: recharts renders nothing under jsdom.
 *
 * THE SIGN CONVENTION, decided rather than inherited (#2955 §4). Since §2 the
 * BARS are magnitudes — both sides rise from a shared baseline — while this
 * tooltip keeps Ausgaben NEGATIVE. That is deliberate and it is the asymmetry
 * the issue asked not to be settled by accident:
 *
 *  - the tooltip's job is to tie a bar to the Buchungen list directly below it,
 *    and that list, the hero figure and ``formatEur`` all show an outflow as
 *    ``−4.000,00 €``. A tooltip reading ``4.000,00 €`` over a list reading
 *    ``−4.000,00 €`` would make the farmer check which one is lying;
 *  - the AXIS is where the magnitude scale belongs, and it now says so
 *    unambiguously by starting at 0;
 *  - and ``Netto`` below only reads as a net at all if its inputs carry signs.
 *
 * The rule in one line: lengths are magnitudes, numbers are signed.
 */
export function ColumnTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: MonthlyColumn }>;
  label?: string;
}) {
  const col = payload?.[0]?.payload;
  if (!active || !col) return null;
  const lines: { key: string; label: string; value: number; sub: boolean; net?: boolean }[] = [
    { key: 'ein', label: 'Einnahmen', value: col.einnahmenEur, sub: false },
  ];
  if (col.einnahmenTransferEur !== 0) {
    lines.push({
      key: 'ein-t',
      label: 'davon Umbuchung',
      value: col.einnahmenTransferEur,
      sub: true,
    });
  }
  lines.push({ key: 'aus', label: 'Ausgaben', value: col.ausgabenEur, sub: false });
  if (col.ausgabenTransferEur !== 0) {
    lines.push({
      key: 'aus-t',
      label: 'davon Umbuchung',
      value: col.ausgabenTransferEur,
      sub: true,
    });
  }
  // #2955 §4 — Netto closes the one thing the paired layout gives up. Two
  // lengths from a shared baseline are far easier to COMPARE than a length up
  // against a length down, but they say nothing about their difference, which
  // the old diverging pair showed for free as the overhang of one over the
  // other. It is a plain sum of the SIGNED fields, which is the whole reason
  // those stayed the source of truth.
  lines.push({
    key: 'netto',
    label: 'Netto',
    value: col.einnahmenEur + col.ausgabenEur,
    sub: false,
    net: true,
  });
  return (
    <div className="liquiditaetsplanung-chart__tooltip">
      <div className="liquiditaetsplanung-chart__tooltip-head">{monthTick(label ?? col.ym)}</div>
      {lines.map((l) => (
        <div
          key={l.key}
          className={`liquiditaetsplanung-chart__tooltip-row${
            l.sub ? ' liquiditaetsplanung-chart__tooltip-row--sub' : ''
          }${l.net ? ' liquiditaetsplanung-chart__tooltip-row--net' : ''}`}
        >
          <span>{l.label}</span>
          <span>{formatEur(l.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function BalanceChart({
  dailySeries,
  kkLimitEur,
  todayYm,
  today,
  selectedYm,
  bandRange,
  txByDate,
  selectedMarker,
  onSelectTx,
  onSelectMonth,
  ledgerRows,
  istHorizon,
  presentation,
  onPresentationChange,
  truncated = false,
}: {
  /** The scope's balance series — drives history, projection, breach shading,
   *  marker + selection. */
  dailySeries: DailyBalancePoint[];
  kkLimitEur: number | null;
  todayYm: string;
  today: string;
  selectedYm?: string;
  /** #2254: an explicit month-range band (a selected Wirtschaftsjahr). When
   *  given it replaces the single-month band derived from ``selectedYm``;
   *  clamped to the fixed chart window by ``ymRangeBandRange``. */
  bandRange?: { startYm: string; endYm: string };
  txByDate?: Map<string, string>;
  selectedMarker?: { date: string; balanceEur: number } | null;
  onSelectTx?: (id: string | null) => void;
  /** Called with the clicked point's ``YYYY-MM`` so the page can select that month. */
  onSelectMonth?: (ym: string) => void;
  /** #2684 Part B — the ledger rows the Buchungen list renders. THE source for
   *  the Säulen presentation, so the bars reconcile with the list by
   *  construction (see ``monthlyColumns``). */
  ledgerRows: LedgerRow[];
  /** The scope's Ist/Prognose boundary date. It grades ROWS (``isActual``),
   *  which is what the hatch splits on since #2955 §3 — months past it used to
   *  hatch whole, and that is the belief §3 exists to retire. The MONTH grain it
   *  still drives is the boundary marker (``isForecast``/``forecastBoundaryYm``). */
  istHorizon: string;
  presentation: ChartPresentation;
  onPresentationChange: (presentation: ChartPresentation) => void;
  /** #2611 ``dataQuality.truncated`` — a capped feed shortens individual bars
   *  invisibly, so column mode says so inline. */
  truncated?: boolean;
}) {
  const data = splitActualPlan(fixedChartWindow(dailySeries, todayYm));
  const hasData = data.some((p) => p.balanceEur != null);

  // #2955 §1 — THE shared window, held once as a date range. A date is the only
  // form that survives the day↔month translation: an index means nothing without
  // the grain that produced it.
  const [sharedRange, setSharedRange] = useState<DateRange>(() => chartWindowRange(todayYm));
  // The fixed window itself moves only when ``todayYm`` does (a new day, or the
  // page's scope changing it). That is the one case where a carried-over range
  // WOULD be stale, so it is also the one case that resets — the job the hook's
  // own reset-on-signature-change effect does in uncontrolled mode.
  const prevTodayYm = useRef(todayYm);
  useEffect(() => {
    if (prevTodayYm.current !== todayYm) {
      prevTodayYm.current = todayYm;
      setSharedRange(chartWindowRange(todayYm));
    }
  }, [todayYm]);

  const dayWindow = useMemo(() => toDayWindow(sharedRange, todayYm), [sharedRange, todayYm]);
  const monthWindow = useMemo(() => toMonthWindow(sharedRange, todayYm), [sharedRange, todayYm]);
  const onDayWindowChange = useCallback(
    (w: IndexWindow) => setSharedRange(fromDayWindow(w, todayYm)),
    [todayYm],
  );
  const onMonthWindowChange = useCallback(
    (w: IndexWindow) => setSharedRange(fromMonthWindow(w, todayYm)),
    [todayYm],
  );

  // #2436 — stock-app zoom/pan over the fixed ±12-month window: the hook slices
  // ``data`` to the visible sub-window; ticks, breach shading, the selected-month
  // band + marker all follow the slice. Reset returns to the full ±12mo view.
  const zoom = useChartTimeWindow(data, {
    ariaLabel: 'Liquiditätskurve (zoom- und verschiebbar)',
    window: dayWindow,
    onWindowChange: onDayWindowChange,
  });
  const visible = zoom.visibleRows;
  const visMin = visible[0]?.date;
  const visMax = visible[visible.length - 1]?.date;

  // #2684 Part B — the Säulen row model, with its window derived from the SAME
  // shared range in the month grain (#2955 §1). Both hooks run on every render
  // (rules of hooks); only the active mode's instance is wired to the toolbar
  // and the plot, and only the active one can therefore write the range back.
  const columns = useMemo(
    () => monthlyColumns(ledgerRows, istHorizon, todayYm),
    [ledgerRows, istHorizon, todayYm],
  );
  // #2955 §1 — the column series is now always 25 months long, so its LENGTH no
  // longer answers "is there a booking to show". The empty state asks the
  // columns whether they carry money, exactly as ``hasData`` asks the curve.
  const hasColumns = useMemo(() => hasColumnData(columns), [columns]);
  // #2743 — how much of the gross is own money moved between the farmer's own
  // accounts. A muted segment is invisible to a screen reader, so it is named
  // in the region's own label and QUANTIFIED in the legend, which the region
  // points at via ``aria-describedby`` below.
  //
  // The numbers deliberately do NOT go in the label itself: this region is
  // focusable and keyboard-operated (``containerProps`` adds ``role="group"``,
  // ``tabIndex`` and the +/−/←/→ handlers), so its accessible NAME is announced
  // in full on every focus — a sentence carrying two percentages would be read
  // out on each of them. The name says what the segment IS; the description
  // says how big it is, in the same words the sighted reader sees.
  const share = useMemo(() => transferShare(columns), [columns]);
  /**
   * #2955 §3 — is any part of any bar drawn hatched? Gates the legend's
   * ``geplant`` entry, exactly as ``share`` gates the Umbuchung one.
   *
   * Over the WHOLE series, not ``colVisible``, for ``transferShare``'s reason:
   * a legend entry that appears and disappears as the reader pans is worse than
   * one that is simply always there. Reading the DRAWN magnitudes rather than
   * re-deriving from ``isActual`` keeps this and the bars answering the same
   * question — a plan part rounded away to 0 draws nothing and is named nothing.
   */
  const hasPlannedShare = useMemo(
    () =>
      columns.some(
        (c) =>
          c.einnahmenPlanOhneTransferEur !== 0 ||
          c.einnahmenPlanTransferEur !== 0 ||
          c.ausgabenPlanOhneTransferBetragEur !== 0 ||
          c.ausgabenPlanTransferBetragEur !== 0,
      ),
    [columns],
  );
  const colZoom = useChartTimeWindow(columns, {
    ariaLabel: share
      ? 'Monatssäulen mit Umbuchungsanteil (zoom- und verschiebbar)'
      : 'Monatssäulen (zoom- und verschiebbar)',
    window: monthWindow,
    onWindowChange: onMonthWindowChange,
  });
  const isColumns = presentation === 'saeulen';
  const activeZoom = isColumns ? colZoom : zoom;

  const presentationToggle = (
    <ChartPresentationToggle
      value={presentation}
      options={PRESENTATIONS}
      onChange={onPresentationChange}
      ariaLabel="Darstellung"
      // HOF-5 — the two glyphs alone did not say what they switch between; the
      // words „Kurve" and „Säulen" do, and they are the same words the rest of
      // the page uses for the two presentations.
      showLabels
    />
  );

  // The two presentations read DIFFERENT sources — the curve the engine's
  // ``dailySeries``, the columns the fact-sourced ``ledgerRows`` — and either
  // can be empty while the other is not. A Kontoauszug-first tenant with no
  // ``liquiditaet_config`` and no planned rows is exactly that case:
  // ``defaultConfig`` anchors off PLANNED rows only, so ``dailySeries`` is []
  // while the ledger lists every bank booking. Gate each mode on its OWN
  // source, and only suppress the toolbar when NEITHER has anything — otherwise
  // the toggle vanishes and the farmer is stranded in the empty mode.
  if (!hasData && !hasColumns) {
    return (
      <div className="liquiditaetsplanung-chart liquiditaetsplanung-chart--empty">
        {isColumns
          ? 'Noch keine Buchungen für die Monatsansicht.'
          : 'Noch keine Daten für die Liquiditätskurve.'}
      </div>
    );
  }

  // Column mode's click target IS a month already (``activeLabel`` is the ym),
  // so it selects the month directly. It deliberately does NOT pick a
  // transaction: a whole month's bar has no "closest" one the way a day does.
  const handleColumnClick = (state: { activeLabel?: string } | null) => {
    const ym = state?.activeLabel;
    if (ym) onSelectMonth?.(ym);
  };

  // ── Säulen: Einnahmen/Ausgaben per month, paired on a shared baseline ──────
  const colVisible = colZoom.visibleRows;
  // #2955 §4 — over the VISIBLE slice, so panning past the boundary takes the
  // marker with it instead of clamping it to an edge.
  const prognoseStartYm = forecastBoundaryYm(colVisible);
  // #3158 — the selected-month band, the columns' counterpart of the curve's
  // ``clampBandToRange`` guard: only a VISIBLE category is placeable, and at
  // month grain membership is the clamp. Empty under a WJ selection (the page
  // passes ``selectedYm={selectedYm || undefined}``) — the WJ ``bandRange``
  // parity is deliberately out of scope for #3158.
  const colBandYm = columnBandYm(selectedYm, colVisible.map((c) => c.ym));
  const truncationNote = truncated ? (
    <p className="liquiditaetsplanung-chart__note" role="status">
      Unvollständige Daten: es konnten nicht alle Buchungen geladen werden, daher können
      einzelne Monatssäulen zu niedrig sein.
    </p>
  ) : null;

  // #2955 §1 — a presentation with NOTHING to draw reports no zoom capability.
  // Its plot is empty either way, but the window is now shared: a gesture made
  // over the empty mode is invisible where it is made and would surface only
  // after switching (the Kontoauszug-first tenant lands on an empty Kurve over a
  // populated ledger, so this is a live path, not a hypothetical). Disabled
  // rather than hidden — ``ChartZoomControls``' own idiom is "no capability =
  // disabled, never a dead click" — and the presentation toggle beside it stays
  // reachable regardless, so the farmer is never stranded in the empty mode.
  const activeHasData = isColumns ? hasColumns : hasData;
  const toolbar = (
    <div className="liquiditaetsplanung-chart__zoom">
      <ChartZoomControls
        canZoomIn={activeHasData && activeZoom.canZoomIn}
        canZoomOut={activeHasData && activeZoom.canZoomOut}
        canPanPast={activeHasData && activeZoom.canPanPast}
        canPanFuture={activeHasData && activeZoom.canPanFuture}
        isDefault={!activeHasData || activeZoom.isDefault}
        onZoomIn={activeZoom.zoomIn}
        onZoomOut={activeZoom.zoomOut}
        onPanPast={activeZoom.panPast}
        onPanFuture={activeZoom.panFuture}
        onReset={activeZoom.reset}
      />
      {presentationToggle}
    </div>
  );

  if (isColumns) {
    return (
      <div className="liquiditaetsplanung-chart">
        {toolbar}
        {!hasColumns ? (
          <div className="liquiditaetsplanung-chart--empty">
            Noch keine Buchungen für die Monatsansicht.
          </div>
        ) : (
          <>
            {truncationNote}
            <div
              className="liquiditaetsplanung-chart__plot"
              {...colZoom.containerProps}
              // #2955 §3 — no longer gated on ``share``. §2 made the legend
              // itself unconditional; leaving the DESCRIPTION gated kept the
              // same defect one layer down, because this region is focusable
              // and keyboard-operated and its description is the only route by
              // which a screen-reader user meets the legend at all. Since §3
              // the legend carries the ``geplant`` texture's name — the one cue
              // that cannot be read off the plot — so it has to be reachable
              // for every tenant, not only those with own-account transfers.
              aria-describedby={COLUMN_LEGEND_ID}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={colVisible}
                  margin={{ top: 8, right: 12, bottom: 0, left: 4 }}
                  onClick={handleColumnClick}
                  barGap={COLUMN_BAR_GAP}
                  barCategoryGap={COLUMN_BAR_CATEGORY_GAP}
                  maxBarSize={COLUMN_MAX_BAR_SIZE}
                >
                  <defs>
                    <ForecastHatch
                      id={COLUMN_HATCH_IDS.income}
                      color={INCOME_GREEN}
                      angleDeg={HATCH_ANGLE_DEG.income}
                    />
                    <ForecastHatch
                      id={COLUMN_HATCH_IDS.expense}
                      color={EXPENSE_RED}
                      angleDeg={HATCH_ANGLE_DEG.expense}
                    />
                    {/* #2986 — the neutral once per SIDE, each taking that side's
                        angle, so every hatched segment in the Einnahmen stack
                        leans one way and every one in the Ausgaben stack the
                        other. */}
                    {share && (
                      <ForecastHatch
                        id={COLUMN_HATCH_IDS.transferIncome}
                        color={TRANSFER_GREY}
                        angleDeg={HATCH_ANGLE_DEG.income}
                      />
                    )}
                    {share && (
                      <ForecastHatch
                        id={COLUMN_HATCH_IDS.transferExpense}
                        color={TRANSFER_GREY}
                        angleDeg={HATCH_ANGLE_DEG.expense}
                      />
                    )}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  {/* #3158 — the same selected-month highlight the Kurve draws,
                      in the same colours. ``x1 === x2 === ym`` spans that month's
                      full category band in recharts 2.x. Declared BEFORE the
                      ``<Bar>``s (and outside the ``<defs>``/Bar ordering, which
                      is load-bearing — see below) so the columns draw over it,
                      mirroring the curve where the band precedes the Lines. */}
                  {colBandYm && (
                    <ReferenceArea
                      x1={colBandYm}
                      x2={colBandYm}
                      fill="#eff6ff"
                      fillOpacity={0.6}
                    />
                  )}
                  {/* Auto-thinned rather than the curve's ``interval={0}``: a
                      multi-year ledger yields far more month categories than fit,
                      and unlike the curve there is no spine-day tick list to
                      pre-thin. ``preserveStartEnd`` keeps both edges labelled. */}
                  <XAxis
                    dataKey="ym"
                    tickFormatter={monthTick}
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  {/* #2955 §2 — both sides are magnitudes now, so the domain
                      collapses from ≈[−3.000k, +3.000k] to [0, 3.000k]: the
                      same pixel height carrying twice the resolution. The axis
                      floor IS the zero baseline, which is why the
                      ``<ReferenceLine y={0}>`` that used to be drawn here (and
                      the ``ZERO_BASELINE_GREY`` it was stroked with) are gone
                      rather than merely unused — see the guard in
                      ``__tests__/chartColors.test.ts``. */}
                  <YAxis
                    tickFormatter={kTick}
                    tick={{ fontSize: 11 }}
                    width={44}
                    domain={[0, 'dataMax']}
                  />
                  <Tooltip content={<ColumnTooltip />} />
                  {/* #2955 §4 — the Ist↔Prognose boundary, the column-mode
                      counterpart of the curve's marker. ``position="start"``
                      puts it on the band EDGE: recharts' default is the band
                      centre, which would strike the first Prognose pair through
                      the middle instead of fencing it off.

                      LABELLED ``Prognose``, NOT ``Heute``, and that is the
                      whole point of the wording: the curve's marker sits at
                      ``today`` while this one sits at the Ist horizon, and the
                      two are NOT the same date whenever the bank data lags the
                      calendar (an August visit against a 30.06. horizon puts
                      this line at July — correct as a boundary, a lie as
                      "today"). It marks what it actually separates, in the same
                      words the page's own „Ist bis … · Prognose ab …" caption
                      uses. */}
                  {prognoseStartYm && (
                    <ReferenceLine
                      x={prognoseStartYm}
                      position="start"
                      stroke="#9ca3af"
                      strokeDasharray="2 3"
                      label={{
                        value: 'Prognose',
                        position: 'insideTopRight',
                        fontSize: 11,
                        fill: '#6b7280',
                      }}
                    />
                  )}
                  {/* #2743 — the two sides keep their OWN ``stackId``, so they
                      stand side by side as a pair; the stack only splits each
                      side INTERNALLY. The operative part is drawn from the
                      baseline outward and the muted transfer sits at the tip,
                      so the bar's total height remains the gross the Buchungen
                      list reconciles against. (Per-month and per-side zeros are
                      handled in ``ColumnTooltip``, not here — recharts keeps a
                      zero-valued series in its default tooltip.)

                      #2955 §2 — Einnahmen is declared FIRST and that is
                      load-bearing, not incidental: recharts lays bar groups out
                      in declaration order, so this is what fixes Einnahmen to
                      the left of Ausgaben. The palette separates at ΔE 7,4,
                      which is legal ONLY with a secondary encoding, and this
                      order is one of the two supplying it (the DOM legend
                      below is the other). Do not reorder these.

                      #2955 §3 — each side is now FOUR entries, still ordered
                      baseline outward: ist-operativ · plan-operativ ·
                      ist-transfer · plan-transfer. The transfer share therefore
                      still ends at the tip and the height is still the gross —
                      ``monthlyColumns`` guarantees the four are a partition of
                      it — while the split by ``LedgerRow.isActual`` lets ONE
                      bar show both states, which is the whole point: the month
                      the Ist horizon falls inside is genuinely mixed.

                      The hatch moved OFF the per-``<Cell>`` overrides in the
                      process. It used to be applied month-by-month on
                      ``isForecast``, i.e. all-or-nothing; now the plan entries
                      are their own always-hatched series and the Ist ones are
                      always solid, which is both finer AND less code — no cell
                      mapping, one ``fill`` per series. */}
                  <Bar
                    dataKey="einnahmenIstOhneTransferEur"
                    name={share ? 'Einnahmen ohne Umbuchungen' : 'Einnahmen'}
                    stackId="einnahmen"
                    fill={INCOME_GREEN}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="einnahmenPlanOhneTransferEur"
                    name={share ? 'Einnahmen ohne Umbuchungen (geplant)' : 'Einnahmen (geplant)'}
                    stackId="einnahmen"
                    fill={`url(#${COLUMN_HATCH_IDS.income})`}
                    // NO fillOpacity, on any of the four hatched Bars — #2986. It
                    // would scale the pattern's stroke along with its wash and put
                    // the pair back under the CVD floor; the fade lives on the
                    // pattern's wash rect. See ``ForecastHatch``.
                    isAnimationActive={false}
                  />
                  {share && (
                    <Bar
                      dataKey="einnahmenIstTransferEur"
                      // Distinct per leg. ``ColumnTooltip`` no longer reads these
                      // (it re-derives its lines from the MonthlyColumn), but a
                      // series identity that is ambiguous in the data model is a
                      // trap for the next reader of this file.
                      name="Umbuchung (Zufluss)"
                      stackId="einnahmen"
                      fill={TRANSFER_GREY}
                      isAnimationActive={false}
                    />
                  )}
                  {share && (
                    <Bar
                      dataKey="einnahmenPlanTransferEur"
                      name="Umbuchung (Zufluss, geplant)"
                      stackId="einnahmen"
                      fill={`url(#${COLUMN_HATCH_IDS.transferIncome})`}
                      isAnimationActive={false}
                    />
                  )}
                  {/* #2955 §2 — the DERIVED magnitudes, never the signed
                      originals: a negative value would draw downward again and,
                      under a ``[0, dataMax]`` domain, simply leave the plot. */}
                  <Bar
                    dataKey="ausgabenIstOhneTransferBetragEur"
                    name={share ? 'Ausgaben ohne Umbuchungen' : 'Ausgaben'}
                    stackId="ausgaben"
                    fill={EXPENSE_RED}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="ausgabenPlanOhneTransferBetragEur"
                    name={share ? 'Ausgaben ohne Umbuchungen (geplant)' : 'Ausgaben (geplant)'}
                    stackId="ausgaben"
                    fill={`url(#${COLUMN_HATCH_IDS.expense})`}
                    isAnimationActive={false}
                  />
                  {share && (
                    <Bar
                      dataKey="ausgabenIstTransferBetragEur"
                      name="Umbuchung (Abfluss)"
                      stackId="ausgaben"
                      fill={TRANSFER_GREY}
                      isAnimationActive={false}
                    />
                  )}
                  {share && (
                    <Bar
                      dataKey="ausgabenPlanTransferBetragEur"
                      name="Umbuchung (Abfluss, geplant)"
                      stackId="ausgaben"
                      fill={`url(#${COLUMN_HATCH_IDS.transferExpense})`}
                      isAnimationActive={false}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
            {
              // A REAL DOM legend rather than recharts' ``<Legend />``: the plot
              // lives inside a ResponsiveContainer, so the in-SVG variant is
              // both untestable under jsdom and unreachable to a screen reader
              // that has already been given the region label above. Mirrors the
              // Ernteprognose legend strip (its ``role="group"`` + swatch span).
              //
              // #2955 §2 — UNCONDITIONAL in column mode, where #2743 gated the
              // whole block behind ``share``. Removing the up/down split removed
              // the cue that told the two sides apart, and the replacement pair
              // separates at ΔE 7,4 — legal only with a secondary encoding, of
              // which this legend is one of exactly two (the other being the
              // fixed within-pair order). Gated on ``transfer_internal`` it was
              // absent for precisely the tenants who have no transfers, i.e.
              // almost all of them. Only the Umbuchung ENTRY stays gated: naming
              // a segment that is not drawn would still be worse than silence.
              <div
                id={COLUMN_LEGEND_ID}
                className="liquiditaetsplanung-chart__legend"
                role="group"
                aria-label="Diagrammlegende"
              >
                {[
                  { key: 'ein', background: INCOME_GREEN, label: 'Einnahmen' },
                  { key: 'aus', background: EXPENSE_RED, label: 'Ausgaben' },
                  ...(share
                    ? [
                        {
                          key: 'transfer',
                          background: TRANSFER_GREY,
                          label: `davon Umbuchung zwischen eigenen Konten${transferShareSuffix(
                            share,
                          )}`,
                        },
                      ]
                    : []),
                  // #2955 §3 — LAST, because it qualifies the entries above
                  // rather than being one of them: the hatch is a state that
                  // any of the three colours can be in. Drawn in the legend's
                  // own text ink rather than in any series colour, so it names
                  // the TEXTURE and does not quietly assert that "geplant"
                  // belongs to Einnahmen.
                  //
                  // Gated on there BEING a planned share, by the same rule as
                  // the Umbuchung entry above and for the same reason — naming
                  // a texture that is not drawn is worse than silence. Like
                  // ``transferShare`` it is evaluated over the WHOLE column
                  // series rather than the zoomed slice, so panning cannot make
                  // the entry blink in and out.
                  //
                  // Labelled just „geplant", with no „(noch nicht gebucht)"
                  // gloss: ``isActual`` is a DATE test against the Ist horizon,
                  // not a record-type test, and under the Alle-Konten aggregate
                  // the horizon is a MINIMUM across accounts — so a genuinely
                  // booked bank row from a further-along account can land on
                  // the planned side, and since #3241 narrowed which accounts
                  // vote, a PLANNED row from a dormant account can land on the
                  // Ist side. „geplant" is the word the curve's own
                  // projection line already uses on the identical predicate;
                  // the parenthetical would have asserted something the flag
                  // does not know.
                  ...(hasPlannedShare
                    ? [
                        {
                          key: 'geplant',
                          background: hatchSwatchBackground(PLANNED_SWATCH_INK),
                          label: 'geplant',
                        },
                      ]
                    : []),
                ].map((entry) => (
                  <span key={entry.key} className="liquiditaetsplanung-chart__legend-entry">
                    <span
                      className="liquiditaetsplanung-chart__legend-swatch"
                      aria-hidden="true"
                      style={{ background: entry.background }}
                    />
                    {entry.label}
                  </span>
                ))}
              </div>
            }
          </>
        )}
      </div>
    );
  }

  // Curve mode with no series, but the ledger DOES have months (the
  // Kontoauszug-first case above). Keep the toolbar so the toggle stays
  // reachable — the other presentation has data.
  if (!hasData) {
    return (
      <div className="liquiditaetsplanung-chart">
        {toolbar}
        <div className="liquiditaetsplanung-chart--empty">
          Noch keine Daten für die Liquiditätskurve.
        </div>
      </div>
    );
  }

  // First spine day of each month across the VISIBLE window (recomputed on the
  // row-slice so labels track zoom/pan, #2436).
  const monthFirsts: string[] = [];
  const seenMonths = new Set<string>();
  for (const p of visible) {
    const ym = p.date.slice(0, 7);
    if (!seenMonths.has(ym)) {
      seenMonths.add(ym);
      monthFirsts.push(p.date);
    }
  }
  // Thin labels so they don't collide: cap at ~12 ticks, keeping the last edge.
  const tickStep = Math.max(1, Math.ceil(monthFirsts.length / 12));
  const monthTicks = monthFirsts.filter(
    (_, i) => i % tickStep === 0 || i === monthFirsts.length - 1,
  );

  // The selected-month / Wirtschaftsjahr highlight band, then CLAMPED to the
  // visible slice so recharts can place it on the zoomed categorical axis (#2436).
  const band = clampBandToRange(
    bandRange ? ymRangeBandRange(bandRange.startYm, bandRange.endYm, todayYm) : monthBandRange(selectedYm, todayYm),
    visMin,
    visMax,
  );
  // Shade the windows where the curve dips below the KK-limit (ties the banner
  // warning to the curve + the flagged ledger rows). Computed over the VISIBLE
  // slice so the shading matches what is on screen.
  const breaches = breachSegments(visible, kkLimitEur);

  // The chart's lower bound (#1509): the min over the plotted series, across the
  // VISIBLE slice so the y-domain fits the current view.
  //
  // Two values, because they answer different questions (HOF-16). The AXIS floor
  // carries the clearance that keeps the KK-Limit rule off the x-axis, and the
  // caption out of the tick strip. The DATA floor is where the money actually
  // reaches, and is what the breach ReferenceArea shades down to — the red band
  // must stay "the area between the curve and the limit line […] only below the
  // limit, not full height" (`liquiditaetsplanung.md`), or a €1 breach draws as a
  // €15.000 block. Wherever the clearance does not bind they are the same number,
  // which is every chart that existed before HOF-16.
  const chartFloor = chartYFloor(visible, kkLimitEur);
  const dataFloor = chartDataFloor(visible, kkLimitEur);

  // HOF-47 — the UPPER bound, which until now was the bare string 'dataMax'
  // while the lower one was a computed function anchored on the KK-limit. That
  // asymmetry WAS the defect: with every plotted value below the limit, the rule
  // is strictly outside the domain and recharts' default `ifOverflow: 'discard'`
  // renders nothing at all — the line is absent, not clipped, from the chart of
  // the farm most in need of it. Reachable across the fixed window for a tenant
  // deep in overdraft, and for ANY breaching tenant by zooming into the dip
  // (#2436 slices the rows into `visible`, and this domain is computed from
  // `visible`). See `chartYCeil` for why the remedy is bounded rather than
  // unconditional.
  const chartCeil = chartYCeil(visible, kkLimitEur);
  const dataCeil = chartDataCeil(visible);
  // The limit is above EVERYTHING plotted — the two branches HOF-47 adds. Near
  // the data (`!kkOffScale`) the ceiling extends and the rule is drawn; far above
  // it the domain is left alone and the fact is carried in words, because a red
  // stroke pinned to the top edge would draw a line where the limit is not.
  const kkAboveAll = kkLimitEur != null && dataCeil != null && kkLimitEur > dataCeil;
  const kkOffScale = kkAboveAll && kkLimitOffScale(visible, kkLimitEur);
  const markerVisible =
    selectedMarker != null && visMin != null && visMax != null &&
    selectedMarker.date >= visMin && selectedMarker.date <= visMax;

  // A click anywhere in the plot snaps to the nearest day (``activeLabel``):
  // select that day's month AND the closest transaction within it, so the
  // picked tx stays visible in the month-filtered ledger (null → empty month
  // clears the selection).
  const handleClick = (state: { activeLabel?: string } | null) => {
    const date = state?.activeLabel;
    if (!date) return;
    onSelectMonth?.(date.slice(0, 7));
    if (onSelectTx && txByDate) onSelectTx(closestTxInMonth(date, txByDate));
  };

  return (
    <div className="liquiditaetsplanung-chart">
      {toolbar}
      <div className="liquiditaetsplanung-chart__plot" {...zoom.containerProps}>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={visible} margin={{ top: 8, right: 12, bottom: 0, left: 4 }} onClick={handleClick}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          {band && (
            <ReferenceArea
              x1={band.x1}
              x2={band.x2}
              fill="#eff6ff"
              fillOpacity={0.6}
              ifOverflow="extendDomain"
            />
          )}
          {kkLimitEur != null &&
            breaches.map((seg) => (
              <ReferenceArea
                key={`breach-${seg.x1}`}
                x1={seg.x1}
                x2={seg.x2}
                y1={kkLimitEur}
                y2={dataFloor}
                fill="#fecaca"
                fillOpacity={0.55}
                ifOverflow="hidden"
              />
            ))}
          <XAxis
            dataKey="date"
            tickFormatter={monthTick}
            tick={{ fontSize: 11 }}
            ticks={monthTicks}
            interval={0}
          />
          <YAxis
            tickFormatter={kTick}
            tick={{ fontSize: 11 }}
            width={44}
            domain={[chartFloor, chartCeil]}
          />
          <Tooltip
            formatter={(v: number, name) => [formatEur(v), name]}
            labelFormatter={(iso: string) =>
              new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE', { timeZone: 'UTC' })
            }
          />
          {/* HOF-5 — the zero baseline. The curve's y-domain floats
              (``chartYFloor`` = min over the visible slice, anchored at the
              KK-limit), so on a farm sitting comfortably in the black nothing on
              the plot says WHERE zero is: a dip toward the bottom of the frame
              reads as "near empty" whether it is +900k or −90k. This is the
              reference that makes the sign of the balance, and the distance from
              it to the KK-limit, readable at a glance.

              Drawn UNDER the KK-limit rule and the curve (declaration order),
              hairline and light, because it is chrome rather than data — and it
              is deliberately not the Säulen mode's retired
              ``ZERO_BASELINE_GREY``: there the axis floor IS zero and bars START
              on the rule, so the fill met the stroke and #2955 §2 removed it.
              Here nothing sits on the line — the curve crosses it — so the
              contrast argument that retired it does not apply. The colour is the
              chart's existing chrome grey (the "Heute" and Prognose markers),
              kept local for the same reason ``PLANNED_SWATCH_INK`` is.
              ``ifOverflow="discard"`` (recharts' default, stated) keeps it off
              the plot entirely on the rare domain that excludes zero.

              HOF-47 gave the KK-Limit rule the opposite guarantee and left this
              one alone, so the two lines now follow different rules on the same
              axis — deliberately, not by omission. Zero is chrome, as the
              paragraph above says in those words; the KK-Limit is the subject of
              an unconditional acceptance criterion (FR-014.4). Giving zero the
              same treatment would stretch the domain to hold a grey hairline on
              every deeply-overdrawn chart, where the sign is not in doubt. */}
          <ReferenceLine
            y={0}
            stroke={ZERO_LINE_GREY}
            strokeWidth={1}
            ifOverflow="discard"
            label={{ value: '0', position: 'insideLeft', fontSize: 10, fill: '#9ca3af' }}
          />
          {/* The KK-Limit rule. HOF-47 gives it two things it did not have:
              a y-domain that can hold it (`chartCeil`), and a caption corner that
              does not collide with „Heute". The caption moves ONLY in the
              above-everything branches — there the rule is near the plot top, so
              recharts' `insideTopRight`, which „Heute" already owns two elements
              down, would put the two captions on top of each other. Everywhere
              else `insideTopRight` stands exactly as HOF-16 left it, hanging
              below the rule in the strip the floor clearance opened. */}
          {kkLimitEur != null && !kkOffScale && (
            <ReferenceLine
              y={kkLimitEur}
              stroke="#b91c1c"
              strokeDasharray="5 4"
              strokeWidth={0.75}
              label={{
                value: 'KK-Limit',
                position: kkAboveAll ? 'insideTopLeft' : 'insideTopRight',
                fontSize: 11,
                fill: '#b91c1c',
              }}
            />
          )}
          {/* The off-scale branch: the limit is so far above the plotted window
              that holding it in the domain would flatten the curve to a straight
              line (`kkLimitOffScale`). The rule is then genuinely off the chart
              and is NOT faked — a red stroke at the top edge would assert a limit
              where there is none. `stroke="none"` renders the line invisible and
              keeps only the label, anchored at the plotted top, which IS the
              domain top in this branch (`'dataMax'`), so recharts' inclusive
              `isInRange` keeps it. „KK-Limit" is the term FR-014 and this rule's
              own caption already carry; the arrow is a glyph, not new vocabulary. */}
          {kkLimitEur != null && kkOffScale && dataCeil != null && (
            <ReferenceLine
              y={dataCeil}
              stroke="none"
              label={{
                value: `↑ KK-Limit ${formatEur(kkLimitEur)}`,
                position: 'insideTopLeft',
                fontSize: 11,
                fill: '#b91c1c',
              }}
            />
          )}
          {/* #2501 — 01.01 splitters before "Heute", so "Heute" stays on top of a
              near-coincident boundary (recharts source z-order). */}
          {yearBoundaryLines(visible.map((r) => r.date))}
          <ReferenceLine
            x={today}
            stroke="#9ca3af"
            strokeDasharray="2 3"
            label={{ value: 'Heute', position: 'insideTopRight', fontSize: 11, fill: '#6b7280' }}
          />
          {markerVisible && selectedMarker && (
            <ReferenceDot
              x={selectedMarker.date}
              y={selectedMarker.balanceEur}
              r={4}
              fill={TOTAL_LINE_BLUE}
              stroke="#ffffff"
              strokeWidth={2}
            />
          )}
          {/* Solid = Ist (history ≤ anchor). */}
          <Line
            type="monotone"
            dataKey="actualEur"
            name="Ist"
            stroke={TOTAL_LINE_BLUE}
            dot={false}
            strokeWidth={2}
            connectNulls={false}
            isAnimationActive={false}
          />
          {/* Dashed projection (> anchor), sharing the boundary vertex with the
              solid history. */}
          <Line
            type="monotone"
            dataKey="planEur"
            name="Geplant"
            stroke={TOTAL_LINE_BLUE}
            strokeDasharray="5 4"
            dot={false}
            strokeWidth={2}
            connectNulls={false}
            isAnimationActive={false}
          />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
